import { environmentHttpClient } from "client/Api/httpClient";
import { ListLocationModel } from "./common";
import { Guid } from "client_legacy/ndaw/guid";
import { NameConflictResolutionKind, UploadResult } from "client/Api/Documents/common";
import { urlEncodeObject } from "client_legacy/ndaw/urlHelper";

export type DocumentDownloadModel = {
	id: number;
	name: string;
};

export type DownloadAsZipModel = {
	location: ListLocationModel;
	documents: readonly DocumentDownloadModel[];
};

export function downloadAsZip(
	model: DownloadAsZipModel
): Promise<string> {
	return environmentHttpClient.post<string>(
		"Documents/SharePoint/FileContent/DownloadAsZip",
		model
	);
}

export function doesServerSupportChunking(
	location: ListLocationModel,
	signal?: AbortSignal
): Promise<boolean> {
	return environmentHttpClient.get<boolean>(
		"Documents/SharePoint/FileContent/DoesServerSupportChunking",
		location,
		{ signal }
	);
}

export type FileUploadModel = {
	file: File;
	fileName: string;
	fileGuid: Guid;
	path: string;
	nameConflictResolution?: NameConflictResolutionKind;
};

export type ChunkUploadModel = {
	file: Blob;
	fileName: string;
	fileGuid: Guid;
	path: string;
	nameConflictResolution?: NameConflictResolutionKind;
	partIndex: number;
	totalParts: number;
	partByteOffset: number;
};

function hasChunkPositionInfo(uploadModel: FileUploadModel | ChunkUploadModel): uploadModel is ChunkUploadModel {
	return Boolean((uploadModel as ChunkUploadModel).totalParts);
}

export function fileUpload(
	location: ListLocationModel,
	parentFolderId: number | null,
	uploadModel: FileUploadModel | ChunkUploadModel,
	signal?: AbortSignal
): Promise<UploadResult> {
	const {
		file,
		fileGuid,
		fileName,
		path,
		nameConflictResolution
	} = uploadModel;

	const formData = new FormData();

	formData.append("file", file);
	formData.append("fileGuid", fileGuid);
	formData.append("fileName", fileName);
	formData.append("path", path);

	if (typeof nameConflictResolution === "number") {
		formData.append("nameConflictResolution", nameConflictResolution.toString());
	}

	if (hasChunkPositionInfo(uploadModel)) {
		const {
			partIndex,
			totalParts,
			partByteOffset
		} = uploadModel;

		formData.append("partIndex", partIndex.toString());
		formData.append("totalParts", totalParts.toString());
		formData.append("partByteOffset", partByteOffset.toString());
	}

	return environmentHttpClient.postFormData<UploadResult>(
		"Documents/SharePoint/FileContent/FileUpload?" + urlEncodeObject({ location, parentFolderId }),
		formData,
		{ signal }
	);
}

export function cancelUpload(
	location: ListLocationModel,
	parentFolderId: number | null,
	fileName: string,
	fileUploadGuid: Guid,
	path: string,
	async: boolean
): Promise<void> {
	return environmentHttpClient.post<void>(
		"Documents/SharePoint/FileContent/CancelUpload",
		{
			location,
			parentFolderId,
			fileName,
			fileUploadGuid,
			path,
		},
		{
			async
		}
	);
}
